import React, {useState} from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  TextField,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useSaveNumberingSorting} from '../../../hooks/productions/useProductions';
import '../Demo.scss';

const DemoNumberingSortingSave: React.FC = () => {
  const [productionId, setProductionId] = useState<number>(59);
  const [productionSecurityGroupId, setProductionSecurityGroupId] =
    useState<number>(1);
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(true);
  const [saveResult, setSaveResult] = useState<string>('');

  // Numbering Settings
  const [subBatesNo, setSubBatesNo] = useState<number>(1);
  const [subBatesNoMinNoLength, setSubBatesNoMinNoLength] = useState<number>(5);
  const [hasNumberPerDocument, setHasNumberPerDocument] =
    useState<boolean>(true);
  const [eNumberingLevel, setENumberingLevel] = useState<number>(1);
  const [isSpecificBatesNo, setIsSpecificBatesNo] = useState<boolean>(true);
  const [eFormatType, setEFormatType] = useState<number>(1);
  const [shouldContinuePreviousNo, setShouldContinuePreviousNo] =
    useState<boolean>(false);
  const [eSpecifyNumberingTypes, setESpecifyNumberingTypes] =
    useState<number>(0);
  const [specificBeginningNo, setSpecificBeginningNo] = useState<number>(1);
  const [specificPrefix, setSpecificPrefix] = useState<string>('T');
  const [specificSuffix, setSpecificSuffix] = useState<string>('');
  const [specificMinNoLength, setSpecificMinNoLength] = useState<number>(0);
  const [fieldID, setFieldID] = useState<number>(-177);
  const [fieldPrefix, setFieldPrefix] = useState<string>('');
  const [fieldSuffix, setFieldSuffix] = useState<string>('');
  const [isSinglepageTextTIFFPDF, setIsSinglepageTextTIFFPDF] =
    useState<boolean>(true);
  const [isOnlyNativeSelected, setIsOnlyNativeSelected] =
    useState<boolean>(false);

  // Sorting Settings
  const [file, setFile] = useState<string>('C:\\fakepath\\Book.xlsx');
  const [isSubSortByFieldASC, setIsSubSortByFieldASC] =
    useState<boolean>(false);
  const [sortByFieldID, setSortByFieldID] = useState<number>(-60);
  const [subSortByFieldID, setSubSortByFieldID] = useState<number>(-57);
  const [sortByIsKeepFamiliesTogether, setSortByIsKeepFamiliesTogether] =
    useState<boolean>(true);
  const [shouldKeepInBegining, setShouldKeepInBegining] =
    useState<boolean>(false);
  const [eKeepDocsWithNoMasterDate, setEKeepDocsWithNoMasterDate] =
    useState<number>(1);
  const [isSortByFieldASC, setIsSortByFieldASC] = useState<boolean>(false);
  const [eSortType, setESortType] = useState<number>(1);
  const [isSortByField, setIsSortByField] = useState<boolean>(true);
  const [isSortByTagOrder, setIsSortByTagOrder] = useState<boolean>(false);
  const [isSortByCustom, setIsSortByCustom] = useState<boolean>(false);
  const [eSortOption, setESortOption] = useState<number>(0);

  const saveNumberingSortingMutation = useSaveNumberingSorting(
    (data) => {
      setSaveResult(`✅ Save successful: ${data.message}`);
    },
    (error) => {
      setSaveResult(`❌ Save failed: ${error.message}`);
    }
  );

  const handleSave = () => {
    const payload = {
      productionnumberingSettings: {
        subBatesNo,
        subBatesNoMinNoLength,
        hasNumberPerDocument,
        eNumberingLevel,
        isSpecificBatesNo,
        eFormatType,
        shouldContinuePreviousNo,
        eSpecifyNumberingTypes,
        specificBeginningNo,
        specificPrefix,
        specificSuffix,
        specificMinNoLength,
        fieldID,
        fieldPrefix,
        fieldSuffix,
        isSinglepageTextTIFFPDF,
        isOnlyNativeSelected,
      },
      productionSortingSettings: {
        file,
        isSubSortByFieldASC,
        sortByFieldID,
        subSortByFieldID,
        sortByIsKeepFamiliesTogether,
        selectedTagList: null,
        shouldKeepInBegining,
        eKeepDocsWithNoMasterDate,
        isSortByFieldASC,
        eSortType,
        isSortByField,
        isSortByTagOrder,
        isSortByCustom,
        eSortOption,
        sortTagOrder: null,
      },
      isMarkComplete,
      productionId,
      productionSecurityGroupId,
    };

    saveNumberingSortingMutation.mutate(payload);
  };

  const handleReset = () => {
    setProductionId(59);
    setProductionSecurityGroupId(1);
    setIsMarkComplete(true);
    setSaveResult('');
    // Reset all form fields to default values
    setSubBatesNo(1);
    setSubBatesNoMinNoLength(5);
    setHasNumberPerDocument(true);
    setENumberingLevel(1);
    setIsSpecificBatesNo(true);
    setEFormatType(1);
    setShouldContinuePreviousNo(false);
    setESpecifyNumberingTypes(0);
    setSpecificBeginningNo(1);
    setSpecificPrefix('T');
    setSpecificSuffix('');
    setSpecificMinNoLength(0);
    setFieldID(-177);
    setFieldPrefix('');
    setFieldSuffix('');
    setIsSinglepageTextTIFFPDF(true);
    setIsOnlyNativeSelected(false);
    setFile('C:\\fakepath\\Book.xlsx');
    setIsSubSortByFieldASC(false);
    setSortByFieldID(-60);
    setSubSortByFieldID(-57);
    setSortByIsKeepFamiliesTogether(true);
    setShouldKeepInBegining(false);
    setEKeepDocsWithNoMasterDate(1);
    setIsSortByFieldASC(false);
    setESortType(1);
    setIsSortByField(true);
    setIsSortByTagOrder(false);
    setIsSortByCustom(false);
    setESortOption(0);
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          💾 Save Numbering & Sorting API Demo
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Test the save numbering and sorting settings endpoint for production
          configuration
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Save Numbering & Sorting
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/numbering-sorting
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Save numbering and sorting settings for production including
            numbering settings, sorting settings, and mark complete status
          </Typography>
        </CardContent>
      </Card>
      {/* Basic Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Basic Parameters
          </Typography>

          <Grid container spacing={2} sx={{marginBottom: 2}}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Production ID"
                type="number"
                value={productionId}
                onChange={(e) => setProductionId(Number(e.target.value))}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Production Security Group ID"
                type="number"
                value={productionSecurityGroupId}
                onChange={(e) =>
                  setProductionSecurityGroupId(Number(e.target.value))
                }
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>

          <FormControlLabel
            data-testid="Mark Complete"
            control={
              <Switch
                checked={isMarkComplete}
                onChange={(e) => setIsMarkComplete(e.target.checked)}
              />
            }
            label="Mark Complete"
          />
        </CardContent>
      </Card>
      {/* Numbering Settings */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className="typo-text subhead3_size">
                Numbering Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sub Bates No"
                    type="number"
                    value={subBatesNo}
                    onChange={(e) => setSubBatesNo(Number(e.target.value))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sub Bates No Min Length"
                    type="number"
                    value={subBatesNoMinNoLength}
                    onChange={(e) =>
                      setSubBatesNoMinNoLength(Number(e.target.value))
                    }
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E Numbering Level"
                    type="number"
                    value={eNumberingLevel}
                    onChange={(e) => setENumberingLevel(Number(e.target.value))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E Format Type"
                    type="number"
                    value={eFormatType}
                    onChange={(e) => setEFormatType(Number(e.target.value))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E Specify Numbering Types"
                    type="number"
                    value={eSpecifyNumberingTypes}
                    onChange={(e) =>
                      setESpecifyNumberingTypes(Number(e.target.value))
                    }
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Specific Beginning No"
                    type="number"
                    value={specificBeginningNo}
                    onChange={(e) =>
                      setSpecificBeginningNo(Number(e.target.value))
                    }
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Specific Prefix"
                    value={specificPrefix}
                    onChange={(e) => setSpecificPrefix(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Specific Suffix"
                    value={specificSuffix}
                    onChange={(e) => setSpecificSuffix(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Specific Min No Length"
                    type="number"
                    value={specificMinNoLength}
                    onChange={(e) =>
                      setSpecificMinNoLength(Number(e.target.value))
                    }
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Field ID"
                    type="number"
                    value={fieldID}
                    onChange={(e) => setFieldID(Number(e.target.value))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Field Prefix"
                    value={fieldPrefix}
                    onChange={(e) => setFieldPrefix(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Field Suffix"
                    value={fieldSuffix}
                    onChange={(e) => setFieldSuffix(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Box sx={{marginTop: 2}}>
                <FormControlLabel
                  data-testid="has-number-per-document"
                  control={
                    <Switch
                      checked={hasNumberPerDocument}
                      onChange={(e) =>
                        setHasNumberPerDocument(e.target.checked)
                      }
                    />
                  }
                  label="Has Number Per Document"
                />
                <FormControlLabel
                  data-testid="is-specific-bates-no"
                  control={
                    <Switch
                      checked={isSpecificBatesNo}
                      onChange={(e) => setIsSpecificBatesNo(e.target.checked)}
                    />
                  }
                  label="Is Specific Bates No"
                />
                <FormControlLabel
                  data-testid="should-continue-previous-no"
                  control={
                    <Switch
                      checked={shouldContinuePreviousNo}
                      onChange={(e) =>
                        setShouldContinuePreviousNo(e.target.checked)
                      }
                    />
                  }
                  label="Should Continue Previous No"
                />
                <FormControlLabel
                  data-testid="is-single-page-text-TIFF-PDF"
                  control={
                    <Switch
                      checked={isSinglepageTextTIFFPDF}
                      onChange={(e) =>
                        setIsSinglepageTextTIFFPDF(e.target.checked)
                      }
                    />
                  }
                  label="Is Single Page Text TIFF PDF"
                />
                <FormControlLabel
                  data-testid="is-only-native-elected"
                  control={
                    <Switch
                      checked={isOnlyNativeSelected}
                      onChange={(e) =>
                        setIsOnlyNativeSelected(e.target.checked)
                      }
                    />
                  }
                  label="Is Only Native Selected"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
      {/* Sorting Settings */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className="typo-text subhead3_size">
                Sorting Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="File"
                    value={file}
                    onChange={(e) => setFile(e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sort By Field ID"
                    type="number"
                    value={sortByFieldID}
                    onChange={(e) => setSortByFieldID(Number(e.target.value))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sub Sort By Field ID"
                    type="number"
                    value={subSortByFieldID}
                    onChange={(e) =>
                      setSubSortByFieldID(Number(e.target.value))
                    }
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E Keep Docs With No Master Date"
                    type="number"
                    value={eKeepDocsWithNoMasterDate}
                    onChange={(e) =>
                      setEKeepDocsWithNoMasterDate(Number(e.target.value))
                    }
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E Sort Type"
                    type="number"
                    value={eSortType}
                    onChange={(e) => setESortType(Number(e.target.value))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="E Sort Option"
                    type="number"
                    value={eSortOption}
                    onChange={(e) => setESortOption(Number(e.target.value))}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>

              <Box sx={{marginTop: 2}}>
                <FormControlLabel
                  data-testid="is-sub-sort-by-field-asc"
                  control={
                    <Switch
                      checked={isSubSortByFieldASC}
                      onChange={(e) => setIsSubSortByFieldASC(e.target.checked)}
                    />
                  }
                  label="Is Sub Sort By Field ASC"
                />
                <FormControlLabel
                  data-testid="sort-by-is-keep-families-together"
                  control={
                    <Switch
                      checked={sortByIsKeepFamiliesTogether}
                      onChange={(e) =>
                        setSortByIsKeepFamiliesTogether(e.target.checked)
                      }
                    />
                  }
                  label="Sort By Is Keep Families Together"
                />
                <FormControlLabel
                data-testid="should-keep-in-beginning-form-control-label"
                  control={
                    <Switch
                      checked={shouldKeepInBegining}
                      onChange={(e) =>
                        setShouldKeepInBegining(e.target.checked)
                      }
                    />
                  }
                  label="Should Keep In Beginning"
                />
                <FormControlLabel
                  data-testid="is-Sort-by-field-asc"
                  control={
                    <Switch
                      checked={isSortByFieldASC}
                      onChange={(e) => setIsSortByFieldASC(e.target.checked)}
                    />
                  }
                  label="Is Sort By Field ASC"
                />
                <FormControlLabel
                  data-testid="is-sort-by-field"
                  control={
                    <Switch
                      checked={isSortByField}
                      onChange={(e) => setIsSortByField(e.target.checked)}
                    />
                  }
                  label="Is Sort By Field"
                />
                <FormControlLabel
                  data-testid="is-sort-by-tag-order"
                  control={
                    <Switch
                      checked={isSortByTagOrder}
                      onChange={(e) => setIsSortByTagOrder(e.target.checked)}
                    />
                  }
                  label="Is Sort By Tag Order"
                />
                <FormControlLabel
                  data-testid="is-sort-by-tag-order"
                  control={
                    <Switch
                      checked={isSortByCustom}
                      onChange={(e) => setIsSortByCustom(e.target.checked)}
                    />
                  }
                  label="Is Sort By Custom"
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
      {/* Action Buttons */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saveNumberingSortingMutation.isPending}
              sx={{minWidth: 120}}
              data-testid="demo-numbering-sorting-save-button"
            >
              {saveNumberingSortingMutation.isPending
                ? 'Saving...'
                : 'Save Settings'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={saveNumberingSortingMutation.isPending}
              data-testid="demo-numbering-sorting-save-reset-button"
            >
              Reset
            </Button>
          </Box>

          {saveNumberingSortingMutation.isPending && (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
              <CircularProgress />
            </Box>
          )}

          {saveResult && (
            <Alert
              severity={saveResult.includes('✅') ? 'success' : 'error'}
              sx={{marginTop: 2}}
            >
              {saveResult}
            </Alert>
          )}
        </CardContent>
      </Card>
      {/* API Details */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            API Details
          </Typography>

          <Box sx={{marginBottom: 2}}>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Request Body:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              {`{
  "productionnumberingSettings": {...},
  "productionSortingSettings": {...},
  "isMarkComplete": boolean,
  "productionId": number,
  "productionSecurityGroupId": number
}`}
            </Box>
          </Box>

          <Box>
            <Typography
              className="typo-text body3_size"
              sx={{fontWeight: 'bold', marginBottom: 1}}
            >
              Response Format:
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            >
              {`{
  "message": "string"
}`}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DemoNumberingSortingSave;
